import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

export type UseHoldActionProps = {
  disabled?: boolean;
  /** Ms between repeated `onStart` calls while held. */
  holdRepeatDelayMs?: number;
  /** Fires immediately on press and on each repeat tick while held. */
  onStart: () => void;
  /** Fires once when the hold ends (release, cancel, blur, tab hidden, or disabled). */
  onStop: () => void;
};

/**
 * Hold-to-repeat pointer interaction for buttons and SVG controls.
 *
 * - Pointer capture keeps the hold alive when the cursor leaves the target.
 * - `onStart` runs immediately, then on an interval until release.
 * - Releases on pointer up/cancel, window blur, hidden tab, or `disabled`.
 *
 * Unmount clears the repeat timer only (no `onStop`) to avoid duplicate stops in Strict Mode;
 * blur/visibility handlers still call `onStop` when the user leaves mid-hold.
 */
export const useHoldAction = ({
  disabled,
  holdRepeatDelayMs = 4000,
  onStart,
  onStop,
}: UseHoldActionProps) => {
  const [active, setActive] = useState(false);
  const holdingRef = useRef(false);
  const intervalRef = useRef<number | null>(null);

  const releaseHold = useCallback(() => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    setActive(false);
    clearIntervalRef(intervalRef);
    onStop();
  }, [onStop]);

  const beginHold = useCallback(() => {
    if (disabled || holdingRef.current) return;
    holdingRef.current = true;
    setActive(true);
    onStart();
    intervalRef.current = window.setInterval(() => {
      if (!holdingRef.current) return;
      onStart();
    }, holdRepeatDelayMs);
  }, [disabled, holdRepeatDelayMs, onStart]);

  const handlePointerDown = useCallback(
    (e: PointerEvent<Element>) => {
      e.preventDefault();
      if (disabled) return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      beginHold();
    },
    [disabled, beginHold]
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent<Element>) => {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      releaseHold();
    },
    [releaseHold]
  );

  useEffect(() => {
    if (disabled && holdingRef.current) {
      releaseHold();
    }

    const endIfHolding = () => {
      if (holdingRef.current) releaseHold();
    };
    const onVisibilityChange = () => {
      if (document.hidden) endIfHolding();
    };

    window.addEventListener('blur', endIfHolding);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('blur', endIfHolding);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearIntervalRef(intervalRef);
      holdingRef.current = false;
    };
  }, [disabled, releaseHold]);

  return {
    active,
    handlePointerDown,
    handlePointerUp,
    handlePointerCancel: releaseHold,
  };
};

const clearIntervalRef = (intervalRef: RefObject<number | null>) => {
  if (intervalRef.current != null) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }
};
